import React, { useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { C, REC_STATUTS, MOIS_COURTS } from '../utils/constants';
import { fmt, fmtUnit, fmtLabel, genMonths } from '../utils/helpers';

// unit : respecte le sélecteur d'unité de la section parente (montants uniquement)
import { exportExcel, rowsToHtml, copyHtmlToClipboard } from '../utils/exports';
import { TableExportBar, SvgExportBox } from '../shared/index';
import InfoTip from './InfoTip';

const RecDonut = ({ segments, total, unit }) => {
  const r = 36, circ = 2 * Math.PI * r;
  let offset = circ * 0.25;
  return (
    <svg width="100%" viewBox="0 0 100 100" style={{ maxWidth: 150, display: 'block', margin: '0 auto' }}>
      <circle cx="50" cy="50" r={r} fill="none" stroke="#f0ebe4" strokeWidth="13" />
      {segments.filter(s => s.value > 0).map((s, i) => {
        const dash = total > 0 ? (s.value / total) * circ : 0;
        const el = (
          <circle key={i} cx="50" cy="50" r={r} fill="none" stroke={s.color} strokeWidth="13"
            strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={offset}
            style={{ transition: 'stroke-dasharray 0.6s ease' }}>
            <title>{s.label} : {fmtUnit(s.value, unit)} ({total > 0 ? Math.round(s.value / total * 100) : 0}%)</title>
          </circle>
        );
        offset -= dash;
        return el;
      })}
      <text x="50" y="47" textAnchor="middle" fontSize="10" fontWeight="800" fill="#444">{fmtUnit(total, unit)}</text>
      <text x="50" y="59" textAnchor="middle" fontSize="7" fill="#bbb">Total</text>
    </svg>
  );
};

const RecLineChart = ({ months, series, unit }) => {
  if (!months?.length) return null;
  const allVals = series.flatMap(s => s.values);
  const maxVal = Math.max(...allVals, 1);
  const W = 500, H = 120, PL = 48, PR = 10, PT = 8, PB = 22;
  const n = months.length;
  const xS = i => PL + (n > 1 ? (i / (n - 1)) * (W - PL - PR) : (W - PL - PR) / 2);
  const yS = v => PT + (1 - v / maxVal) * (H - PT - PB);
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginBottom: 8, fontSize: '0.62rem' }}>
        {series.map(s => (
          <span key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 14, height: 2, background: s.color, display: 'inline-block', borderRadius: 1 }} />{s.label}
          </span>
        ))}
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ minWidth: Math.max(n * 28, 200), display: 'block' }}>
        {[0, 0.5, 1].map(f => <line key={f} x1={PL} x2={W - PR} y1={yS(maxVal * f)} y2={yS(maxVal * f)} stroke="#f0ebe4" strokeWidth="1" />)}
        {[0.5, 1].map(f => <text key={f} x={PL - 3} y={yS(maxVal * f) + 3} textAnchor="end" fontSize="8" fill="#bbb">{fmtUnit(maxVal * f, unit)}</text>)}
        {series.map(s => {
          const pts = s.values.map((v, i) => `${xS(i)},${yS(v)}`).join(' ');
          return (
            <g key={s.key}>
              <polyline points={pts} fill="none" stroke={s.color} strokeWidth="2" />
              {s.values.map((v, i) => v > 0 ? <circle key={i} cx={xS(i)} cy={yS(v)} r="2.5" fill={s.color}><title>{fmtLabel(months[i])} — {s.label} : {fmtUnit(v, unit)}</title></circle> : null)}
            </g>
          );
        })}
        {months.map((m, i) => <text key={i} x={xS(i)} y={H - 4} textAnchor="middle" fontSize="8" fill="#bbb">{MOIS_COURTS[parseInt(m.slice(5, 7), 10) - 1]}</text>)}
      </svg>
    </div>
  );
};

const PTF_STATUTS = ['RECEPTION', 'PTF', 'A_NE_PAS_VERSER_R', 'A_NE_PAS_VERSER_NR'];

const PtfBarChart = ({ months, values, ptfAvant, ptfApres, ptfAvantRows, ptfApresRows, onBarClick, debut, unit }) => {
  if (!months?.length) return null;
  const allVals = [...(ptfAvant > 0 ? [ptfAvant] : []), ...values, ...(ptfApres > 0 ? [ptfApres] : [])];
  const max = Math.max(...allVals, 1);
  const totalCols = months.length + (ptfAvant > 0 ? 1 : 0) + (ptfApres > 0 ? 1 : 0);
  const W = 500, H = 130, PL = 48, PR = 10, PT = 8, PB = 30;
  const colW = (W - PL - PR) / totalCols;
  const barW = Math.max(4, colW - 4);
  let colIdx = 0;
  const xC = () => PL + (colIdx + 0.5) * colW;
  const yS = v => PT + (1 - v / max) * (H - PT - PB);

  const bars = [];
  const clickStyle = onBarClick ? { cursor: 'pointer' } : {};

  if (ptfAvant > 0) {
    const x = PL + (colIdx + 0.5) * colW;
    bars.push(
      <g key="avant" style={clickStyle} onClick={() => onBarClick && onBarClick({ label: `PTF — Avant ${debut?.slice(0,4) || ''}`, ptfRows: ptfAvantRows, statuts: PTF_STATUTS })}>
        <rect x={x - barW / 2} y={yS(ptfAvant)} width={barW} height={Math.max(0, H - PB - yS(ptfAvant))} fill="#90caf9" rx="2">
          <title>Cumul avant période : {fmtUnit(ptfAvant, unit)}</title>
        </rect>
        <text x={x} y={yS(ptfAvant) - 3} textAnchor="middle" fontSize="7" fill="#1565c0">{fmtUnit(ptfAvant, unit)}</text>
        <text x={x} y={H - 16} textAnchor="middle" fontSize="7" fill="#888">Avant</text>
        <text x={x} y={H - 6} textAnchor="middle" fontSize="6" fill="#aaa">cumul</text>
      </g>
    );
    colIdx++;
  }

  values.forEach((v, i) => {
    const x = PL + (colIdx + 0.5) * colW;
    bars.push(
      <g key={i} style={v > 0 ? clickStyle : {}} onClick={() => v > 0 && onBarClick && onBarClick({ label: `PTF — ${fmtLabel(months[i])}`, statuts: PTF_STATUTS, month: months[i] })}>
        <rect x={x - barW / 2} y={yS(v)} width={barW} height={Math.max(0, H - PB - yS(v))} fill="#1565c0" rx="2" opacity="0.85">
          <title>{fmtLabel(months[i])} : {fmtUnit(v, unit)}</title>
        </rect>
        {v > 0 && <text x={x} y={yS(v) - 3} textAnchor="middle" fontSize="7" fill="#1565c0">{fmtUnit(v, unit)}</text>}
        <text x={x} y={H - 4} textAnchor="middle" fontSize="8" fill="#bbb">{MOIS_COURTS[parseInt(months[i].slice(5, 7), 10) - 1]}</text>
      </g>
    );
    colIdx++;
  });

  if (ptfApres > 0) {
    const x = PL + (colIdx + 0.5) * colW;
    bars.push(
      <g key="apres" style={clickStyle} onClick={() => onBarClick && onBarClick({ label: `PTF — Après ${debut?.slice(0,4) || ''}`, ptfRows: ptfApresRows, statuts: PTF_STATUTS })}>
        <rect x={x - barW / 2} y={yS(ptfApres)} width={barW} height={Math.max(0, H - PB - yS(ptfApres))} fill="#90caf9" rx="2">
          <title>Cumul après période : {fmtUnit(ptfApres, unit)}</title>
        </rect>
        <text x={x} y={yS(ptfApres) - 3} textAnchor="middle" fontSize="7" fill="#1565c0">{fmtUnit(ptfApres, unit)}</text>
        <text x={x} y={H - 16} textAnchor="middle" fontSize="7" fill="#888">Après</text>
        <text x={x} y={H - 6} textAnchor="middle" fontSize="6" fill="#aaa">cumul</text>
      </g>
    );
  }

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ minWidth: Math.max(totalCols * 28, 200), display: 'block' }}>
      {[0, 0.5, 1].map(f => <line key={f} x1={PL} x2={W - PR} y1={yS(max * f)} y2={yS(max * f)} stroke="#f0ebe4" strokeWidth="1" />)}
      {[0.5, 1].map(f => <text key={f} x={PL - 3} y={yS(max * f) + 3} textAnchor="end" fontSize="8" fill="#bbb">{fmtUnit(max * f, unit)}</text>)}
      {bars}
    </svg>
  );
};

const RecouvrementSection = ({ detailRec, loadDR, debut, fin, ptfAvant = 0, ptfApres = 0, ptfAvantRows = [], ptfApresRows = [], onStatutClick, onPtfBarClick, unit }) => {
  const { segments, total, months, series, projetRows } = useMemo(() => {
    const months = genMonths(debut, fin);
    const totals = {}, monthly = {}, projets = {};
    REC_STATUTS.forEach(s => {
      totals[s.key]  = { montant: 0, nb: 0 };
      monthly[s.key] = {};
      months.forEach(m => { monthly[s.key][m] = 0; });
    });
    detailRec.forEach(r => {
      const u  = (r.statut || '').toUpperCase();
      const m  = (r.dateReglement || '').slice(0, 7);
      const st = REC_STATUTS.find(s => s.values.includes(u));
      if (!st) return;
      totals[st.key].montant += r.montant || 0;
      totals[st.key].nb      += 1;
      if (m in monthly[st.key]) monthly[st.key][m] += r.montant || 0;
      const proj = r.projet || '—';
      if (!projets[proj]) projets[proj] = {};
      projets[proj][st.key] = (projets[proj][st.key] || 0) + (r.montant || 0);
    });
    const total    = REC_STATUTS.reduce((s, st) => s + totals[st.key].montant, 0);
    const segments = REC_STATUTS.map(s => ({ ...s, value: totals[s.key].montant, nb: totals[s.key].nb, pct: total > 0 ? Math.round(totals[s.key].montant / total * 100) : 0 }));
    const series   = REC_STATUTS.map(s => ({ ...s, values: months.map(m => monthly[s.key][m] || 0) }));
    const projetRows = Object.entries(projets).map(([projet, vals]) => ({
      projet,
      ...Object.fromEntries(REC_STATUTS.map(s => [s.key, vals[s.key] || 0])),
      total: REC_STATUTS.reduce((sum, s) => sum + (vals[s.key] || 0), 0),
    })).sort((a, b) => b.total - a.total);
    return { segments, total, months, series, projetRows };
  }, [detailRec, debut, fin]);

  if (!loadDR && !detailRec.length) return null;

  return (
    <div style={{ background: '#fff', borderRadius: 10, border: `1px solid ${C.border}`, marginTop: 16, padding: '14px 18px', boxShadow: '0 1px 4px rgba(109,78,54,0.06)' }}>
      <div style={{ fontWeight: 700, color: '#2e5e2e', fontSize: '0.88rem', marginBottom: 14, display: 'flex', alignItems: 'center' }}>
        Analyse Recouvrement par Statut
        <InfoTip text="Répartition des encaissements par statut bancaire sur la période. Cliquez sur un statut pour filtrer le tableau de détail." />
      </div>

      {loadDR ? <div style={{ textAlign: 'center', padding: 30 }}><Spinner /></div> : (<>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 20 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, flex: '2 1 380px' }}>
            {segments.map(s => (
              <div key={s.key} onClick={() => onStatutClick && onStatutClick(s)}
                style={{ flex: '1 1 150px', borderRadius: 10, border: `1px solid ${s.color}33`, borderLeft: `4px solid ${s.color}`, background: s.bg, padding: '10px 14px', cursor: onStatutClick ? 'pointer' : 'default' }}>
                <div style={{ fontSize: '0.62rem', fontWeight: 700, color: s.color, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4, display: 'flex', alignItems: 'center' }}>
                  {s.label}<InfoTip text={s.info} />
                </div>
                <div style={{ fontSize: '1.35rem', fontWeight: 800, color: s.color, lineHeight: 1.1 }}>{fmtUnit(s.value, unit)}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span style={{ fontSize: '0.65rem', color: '#888' }}>{s.nb} règl.</span>
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, color: s.color }}>{s.pct}%</span>
                </div>
                <div style={{ marginTop: 5, height: 4, borderRadius: 2, background: `${s.color}22`, overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 2, background: s.color, width: `${s.pct}%`, transition: 'width 0.5s ease' }} />
                </div>
              </div>
            ))}
          </div>
          <div style={{ flex: '0 0 170px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <SvgExportBox filename="recouvrement-donut" style={{ width: '100%' }}>
              <RecDonut segments={segments} total={total} unit={unit} />
            </SvgExportBox>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
              {segments.map(s => (
                <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.63rem' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                  <span style={{ color: '#666', flex: 1 }}>{s.label}</span>
                  <span style={{ color: s.color, fontWeight: 700 }}>{s.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginBottom: 20 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', marginBottom: 10, display: 'flex', alignItems: 'center' }}>
            Évolution mensuelle par statut
            <InfoTip text="Courbe des montants encaissés mois par mois, groupés par statut. Basé sur la date de règlement (ou date de saisie si règlement absent). Permet de visualiser les tendances et pics de chaque catégorie." />
          </div>
          <SvgExportBox filename="recouvrement-evolution">
            <RecLineChart months={months} series={series} unit={unit} />
          </SvgExportBox>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14, marginBottom: 20 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1565c0', marginBottom: 10, display: 'flex', alignItems: 'center' }}>
            PTF par mois
            <InfoTip text="Montants PTF (RECEPTION, PTF, A_NE_PAS_VERSER_R, A_NE_PAS_VERSER_NR) par mois de règlement. La barre « Avant » est le cumul de tous les PTF antérieurs à la période, la barre « Après » le cumul de ceux postérieurs — pour visualiser le stock restant hors période." />
          </div>
          <SvgExportBox filename="ptf-par-mois" style={{ overflowX: 'auto' }}>
            <PtfBarChart months={months} values={series.find(s => s.key === 'ptf')?.values || []} ptfAvant={ptfAvant} ptfApres={ptfApres} onBarClick={onPtfBarClick} ptfAvantRows={ptfAvantRows} ptfApresRows={ptfApresRows} debut={debut} unit={unit} />
          </SvgExportBox>
        </div>

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 14 }}>
          <TableExportBar style={{ marginBottom: 6 }}
            onExcel={() => exportExcel(projetRows.map(r => ({ 'Projet': r.projet, ...Object.fromEntries(REC_STATUTS.map(s => [s.label, r[s.key]])), 'Total': r.total })), 'recouvrement-par-projet')}
            onCopy={() => { const { html, plain } = rowsToHtml(projetRows.map(r => ({ 'Projet': r.projet, ...Object.fromEntries(REC_STATUTS.map(s => [s.label, fmt(r[s.key])])), 'Total': fmt(r.total) }))); copyHtmlToClipboard(html, plain); }}
          />
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#555', marginBottom: 10, display: 'flex', alignItems: 'center' }}>
            Synthèse par Projet
            <InfoTip text="Cumul des montants encaissés par projet et par statut sur la période sélectionnée. Triés par total décroissant." />
          </div>
          <div style={{ overflowX: 'auto', borderRadius: 6, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
              <thead>
                <tr style={{ background: '#fdf8f4' }}>
                  <th style={{ padding: '7px 12px', textAlign: 'left', fontWeight: 700, fontSize: '0.7rem', color: '#888', borderBottom: '2px solid #d4c8bc', borderRight: '2px solid #d4c8bc' }}>Projet</th>
                  {REC_STATUTS.map(s => <th key={s.key} style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, fontSize: '0.7rem', color: s.color, borderBottom: '2px solid #d4c8bc', whiteSpace: 'nowrap' }}>{s.label}</th>)}
                  <th style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, fontSize: '0.7rem', color: '#444', borderBottom: '2px solid #d4c8bc', borderLeft: '2px solid #d4c8bc' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {projetRows.map((row, i) => (
                  <tr key={row.projet} style={{ borderBottom: '1px solid #f0ebe4', background: i % 2 === 0 ? '#fff' : '#fdfaf7' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f9f3ed'}
                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fdfaf7'}>
                    <td style={{ padding: '7px 12px', fontWeight: 600, color: '#333', borderRight: '2px solid #d4c8bc' }}>{row.projet}</td>
                    {REC_STATUTS.map(s => <td key={s.key} style={{ padding: '7px 12px', textAlign: 'right', color: s.color, fontWeight: row[s.key] > 0 ? 600 : 400 }}>{row[s.key] > 0 ? fmt(row[s.key]) : '—'}</td>)}
                    <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: '#333', borderLeft: '2px solid #d4c8bc' }}>{fmt(row.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f5ede4', borderTop: '2px solid #d4c8bc' }}>
                  <td style={{ padding: '7px 12px', fontWeight: 700, color: '#5c3317', fontSize: '0.75rem', borderRight: '2px solid #d4c8bc' }}>TOTAL</td>
                  {segments.map(s => <td key={s.key} style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: s.color }}>{fmt(s.value)}</td>)}
                  <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 700, color: '#5c3317', borderLeft: '2px solid #d4c8bc' }}>{fmt(total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </>)}
    </div>
  );
};

export default RecouvrementSection;
